import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Activity, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CompactHero } from "@/components/brand/CompactHero";
import { HeroMetaItem, HeroMetaSep } from "@/components/brand/Hero";
import { Pill, type PillTone } from "@/components/brand/Pill";
import { Card, SectionHead, Mantra, MantraAccent } from "@/components/brand/Card";
import {
  Toolbar,
  ToolbarChip,
  ToolbarSearch,
  ToolbarSpacer,
  Legend,
} from "@/components/brand/Toolbar";
import { CatalogPager } from "@/components/agents/cleancore/CatalogPager";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorState } from "@/components/ErrorState";
import { useProject, useProjectRuns, useProjectRunStats } from "@/hooks/useApi";
import { useResettingPage } from "@/hooks/useResettingPage";
import { pipelineToneLabel, pipelineToneStyle, type PipelineTone } from "@/lib/pipeline-utils";
import { AGENT_DISPLAY_NAMES } from "@/lib/agent-ids";
import { detailedTokens, detailedUsd } from "@/lib/format";
import { formatRelative } from "@/lib/datetime";
import { elapsedMs, formatDuration } from "@/lib/code-companion-session";
import { runModelLabel } from "@/lib/model-label";
import { tp } from "@/lib/plural";
import type { ProjectRun } from "@/api/agents";

/**
 * Runs — #867.
 *
 * Até esta mudança a página era 100% inventada: um array de seis execuções,
 * quatro KPIs cravados, uma timeline de seis nós escrita à mão e um bloco de
 * tokens com números que ninguém mediu. O único dado real era o nome do
 * projeto.
 *
 * O problema não era o array, era ele ser **plausível**. Um projeto
 * recém-criado, sem nenhuma execução, anunciava 27 runs e uma em andamento —
 * e ninguém desconfia de "96% de sucesso".
 *
 * (Os literais do mock estão descritos, não citados: `project-runs-reais.test.tsx`
 * varre este arquivo atrás deles.)
 *
 * Saíram sem substituto, por não existir a medição: a coluna Trigger (não há
 * coluna nem chave que diga se a run veio de disparo manual, agenda ou
 * webhook) e os stage dots por nó (persistência por nó só existe atrás de
 * `AGENTS_ENABLE_NODE_DEBUG`, sem rota que a sirva). O estado da run continua,
 * que é o que de fato se mede.
 *
 * Saíram os controles sem destino: exportar log (não há rota) e disparar uma
 * run (não há para onde ir sem escolher um agente antes — o disparo mora na
 * página do agente). Enfeite é pior que ausência: quem clica em "Status" e vê
 * a mesma lista conclui que não há falha, quando não houve filtro.
 *
 * `formatDuration`/`elapsedMs` vêm de `code-companion-session` em vez de uma
 * cópia local: o `elapsedMs` já resolve "sem `completed_at` → conta até agora",
 * que é o caso da run em andamento. O nome do módulo é herança — as duas são
 * genéricas e caberiam melhor num `lib/duration.ts`.
 */

/** Alinhado com o default do backend (`limit: int = Query(20, ...)`). */
const PAGE_SIZE = 20;

const SEARCH_DEBOUNCE_MS = 500;

/**
 * Select de toolbar. A classe devia morar no `Toolbar.tsx`, junto do
 * `ToolbarSearch` e do `ToolbarChip`; a outra cópia saiu com a grade por tabela
 * do Configuration Server, e quem precisar dela de novo a leva para lá.
 */
const TOOLBAR_SELECT =
  "h-7 rounded-md border border-[#e8ebf0] bg-white px-2 font-mono text-[11px] " +
  "tracking-[0.04em] text-[#596b85] outline-none focus:border-[#ffc83d] " +
  "focus:ring-2 focus:ring-[rgba(255,200,61,0.25)]";

/** Janelas do recorte. `null` é "sem recorte", não "zero dia". */
const WINDOWS = [
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
  { days: null, label: "All time" },
] as const;

/** Id curto, como o hero da página do agente já mostra. */
function shortId(id: string): string {
  return id.slice(0, 5).toUpperCase();
}

const TERMINAL: ReadonlySet<ProjectRun["status"]> = new Set(["completed", "failed"]);

/**
 * Duração legível de uma run.
 *
 * Em curso conta até agora; terminada conta até o fim gravado. **Terminada sem
 * `completed_at` não conta**: o `elapsedMs` cairia no "até agora" e uma run que
 * falhou semana passada apareceria com oito dias de duração. O executor agora
 * sempre grava o fim, mas linha antiga não tem — e travessão é o que se sabe.
 */
function runDuration(run: ProjectRun): string | null {
  if (TERMINAL.has(run.status) && !run.completed_at) return null;
  return formatDuration(elapsedMs(run.started_at, run.completed_at));
}

/**
 * Estado da run → vocabulário de pipeline já existente.
 *
 * `paused` vira `decision` (rótulo "Awaiting your decision") porque é o que ela
 * é: a pausa desta plataforma é o interrupt de HITL, parado à espera de gente.
 * Chamar de "running" diria que a máquina está trabalhando.
 *
 * **As chaves são exatamente os estados que `agent_executions` guarda**, porque
 * este mapa é também a lista do filtro de status. Os dois escritores da tabela
 * gravam só estes quatro: o `persistence.py` do agents, e o Code Companion, que
 * traduz os dele pelo `_STATUS_MAP` de `cc_run_execution.py` (`queued` vira
 * `running`) e levanta em estado desconhecido. `pending` estava aqui, vindo do
 * comentário original da coluna, e oferecia no filtro uma opção que sempre
 * devolvia lista vazia. Estado novo que apareça no banco cai no ramo de status
 * desconhecido, e aparece como veio.
 */
const STATUS_TONE = {
  running: "running",
  completed: "done",
  failed: "failed",
  paused: "decision",
} as const satisfies Record<string, PipelineTone>;

/** Os estados que esta tela sabe desenhar — é também a lista do filtro. */
type KnownStatus = keyof typeof STATUS_TONE;

const PILL_TONE: Record<PipelineTone, PillTone> = {
  running: "gold",
  done: "ok",
  decision: "warn",
  failed: "danger",
  next: "navy",
  waiting: "muted",
};

const STATUSES = Object.keys(STATUS_TONE) as KnownStatus[];

/**
 * O tom de um estado, ou `null` quando a tela não o conhece.
 *
 * O contrato diz `status: string`, que é o que o backend grava. Um estado novo
 * não pode herdar o tom de outro: `?? "waiting"` fazia a run aparecer
 * "Waiting", que é uma afirmação sobre ela. Desconhecido se mostra como veio.
 */
function statusTone(status: string): PipelineTone | null {
  // `hasOwnProperty`, e não `in`: `in` acharia `toString` e `constructor` no
  // protótipo, e um status "constructor" ganharia tom. O `Object.hasOwn` faria
  // o mesmo mais curto, mas é ES2022 e o projeto compila para ES2020.
  return Object.prototype.hasOwnProperty.call(STATUS_TONE, status)
    ? STATUS_TONE[status as KnownStatus]
    : null;
}

export default function ProjectRuns() {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();
  const { data: project } = useProject(projectId);
  const { data: stats } = useProjectRunStats(projectId);
  const [searchParams, setSearchParams] = useSearchParams();

  const [status, setStatus] = useState("");
  // O agente mora na URL (`?agent=`), não no estado: o "Past Runs" do Functional
  // chega por ela, e o F5 e o link copiado mantêm o recorte. Só vale agente que
  // o select oferece — um recorte que ele não sabe mostrar esvaziaria a lista
  // com o filtro aparentando "todos".
  const agentFromUrl = searchParams.get("agent") ?? "";
  const agentType = Object.prototype.hasOwnProperty.call(AGENT_DISPLAY_NAMES, agentFromUrl)
    ? agentFromUrl
    : "";

  function setAgentType(value: string) {
    // `replace`: trocar o filtro não é navegar. Sem ele, o "voltar" do navegador
    // desfaria filtro por filtro antes de sair da tela.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set("agent", value);
        else next.delete("agent");
        return next;
      },
      { replace: true },
    );
  }

  // Agente que o select não oferece sai da URL também, não só do filtro: senão
  // o link copiado daqui carregaria o valor inválido adiante, e a tela que o
  // recebesse teria de ignorá-lo de novo.
  const invalidAgentInUrl = agentFromUrl !== "" && agentType === "";
  useEffect(() => {
    if (!invalidAgentInUrl) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("agent");
        return next;
      },
      { replace: true },
    );
  }, [invalidAgentInUrl, setSearchParams]);

  const [windowDays, setWindowDays] = useState<number | null>(null);
  // O "agora" de onde a janela é medida. Nasce quando a tela monta e só o
  // Refresh o move. Recalcular `Date.now()` a cada troca de chip fazia 7d →
  // 30d → 7d gerar três timestamps com milissegundos diferentes — três chaves
  // de cache, nenhuma reaproveitada — e, pior, o Refresh não movia a janela:
  // "últimos 7 dias" com a aba aberta desde de manhã virava 7 dias e umas horas.
  const [windowAnchor, setWindowAnchor] = useState(() => Date.now());
  // Buffer local do que se digita. O que vai ao backend é o `search`, atrasado
  // — senão é um request por tecla.
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const dateFrom = useMemo(() => {
    if (windowDays === null) return undefined;
    return new Date(windowAnchor - windowDays * 86_400_000).toISOString();
  }, [windowDays, windowAnchor]);

  const filters = useMemo(
    () => ({
      status: status || undefined,
      agentType: agentType || undefined,
      q: search || undefined,
      dateFrom,
    }),
    [status, agentType, search, dateFrom],
  );

  // A página fica amarrada ao recorte em que foi escolhida. Sem isto, filtrar
  // estando na página 2 pediria a página 2 do recorte novo — que pode não
  // existir — e a tela desenharia "nenhum resultado" para um filtro que tem
  // resultado na página 1.
  const [page, setPage] = useResettingPage(JSON.stringify(filters));

  const {
    data: runs,
    isLoading: runsLoading,
    isError: runsError,
    refetch: refetchRuns,
  } = useProjectRuns(projectId, {
    ...filters,
    page,
    limit: PAGE_SIZE,
  });

  const items = runs?.items ?? [];
  const total = runs?.total ?? 0;
  const hasFilter = Boolean(status || agentType || search || dateFrom);

  const avgDuration =
    stats?.avg_duration_seconds != null
      ? formatDuration(stats.avg_duration_seconds * 1000)
      : null;

  function clearFilters() {
    setStatus("");
    setAgentType("");
    setWindowDays(null);
    setSearchInput("");
    setSearch("");
  }

  function refresh() {
    // Com janela ativa, mover a âncora já muda a chave e dispara a busca nova;
    // o `invalidate` cobre o caso sem janela, em que a chave não muda.
    setWindowAnchor(Date.now());
    queryClient.invalidateQueries({ queryKey: ["projectRuns"] });
    queryClient.invalidateQueries({ queryKey: ["projectRunStats"] });
  }

  return (
    <div className="-m-6">
      <CompactHero
        glyph={<Activity />}
        live={(stats?.running_count ?? 0) > 0}
        overline={`${(project?.name || "PROJECT").toUpperCase()} · ${t("EXECUTION HISTORY")}`}
        title={t("Runs")}
        meta={
          <>
            <HeroMetaItem k={t("Project")} v={project?.name || "—"} />
            <HeroMetaSep />
            <HeroMetaItem
              k={t("Last run")}
              v={stats?.last_run ? formatRelative(stats.last_run) : "—"}
            />
          </>
        }
        kpis={[
          // Sem `stats` — carregando ou com erro —, travessão. O `?? 0` que
          // estava aqui dizia "o projeto nunca rodou nada" toda vez que a
          // consulta falhava, e ninguém desconfia de um zero.
          { label: t("Total Runs"), value: stats ? String(stats.execution_count) : "—" },
          {
            label: t("Running Now"),
            value: stats ? String(stats.running_count) : "—",
            gold: (stats?.running_count ?? 0) > 0,
          },
          // Ausência vira travessão, não zero: run nenhuma concluída não é
          // "duração média de zero segundo".
          { label: t("Avg. Duration"), value: avgDuration ?? "—" },
          {
            label: t("Success Rate"),
            // Sem run terminal não existe taxa. O backend manda `null` de
            // propósito — anunciar 100% num projeto que nunca rodou é o
            // indicador inventado que este issue remove.
            value: stats?.success_rate_pct != null ? String(stats.success_rate_pct) : "—",
            unit: stats?.success_rate_pct != null ? "%" : undefined,
            // Sem `stats` não há o que dizer: "No finished run yet" afirmaria
            // algo que a consulta que falhou não chegou a responder.
            sub: !stats
              ? undefined
              : stats.success_rate_pct != null
                ? tp(t, stats.failed_count, "1 failure", "{{n}} failures")
                : t("No finished run yet"),
          },
        ]}
      />

      <div className="p-8 space-y-7">
        <section>
          <SectionHead
            overline={t("THE LOG")}
            title={t("Run history")}
            // Só com a lista de pé. Com o refetch falhando, o react-query
            // guarda o último dado bom em `data` e liga `isError`: o contador
            // lia o velho e dizia "16 RUNS" em cima de "não foi possível
            // carregar". E antes do primeiro dado, `total ?? 0` anunciava
            // "0 RUNS" em cima do skeleton.
            tag={runs && !runsError ? tp(t, total, "1 run", "{{n}} runs") : undefined}
            sub={t("Sorted by most recent.")}
            right={
              <Legend
                chips={(["done", "running", "failed"] as const).map((tone) => ({
                  label: pipelineToneLabel(tone, t).toUpperCase(),
                  color: pipelineToneStyle[tone].dot,
                  glow: pipelineToneStyle[tone].glow,
                }))}
              />
            }
          />

          <Card>
            <Toolbar>
              {/* O que a busca casa de verdade: `ae.id::text`, `ae.agent_type`
                  e `u.name`. O agente tem seletor próprio ao lado, porque o
                  nome de exibição ("Configuration Server") não bate com o
                  `agent_type` ("configuration_server") e prometer isso aqui seria
                  prometer uma busca que devolve vazio. */}
              <ToolbarSearch
                placeholder={t("Search by run ID or who triggered it…")}
                value={searchInput}
                onChange={setSearchInput}
                onKeyDown={(event) => {
                  if (event.key === "Enter") setSearch(searchInput.trim());
                }}
              />
              <select
                aria-label={t("Agent")}
                className={TOOLBAR_SELECT}
                value={agentType}
                onChange={(event) => setAgentType(event.target.value)}
              >
                <option value="">{t("All agents")}</option>
                {Object.entries(AGENT_DISPLAY_NAMES).map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
              <select
                aria-label={t("Status")}
                className={TOOLBAR_SELECT}
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="">{t("All statuses")}</option>
                {STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {pipelineToneLabel(STATUS_TONE[value], t)}
                  </option>
                ))}
              </select>
              {WINDOWS.map((w) => (
                <ToolbarChip
                  key={w.label}
                  active={windowDays === w.days}
                  onClick={() => setWindowDays(w.days)}
                >
                  {w.days === null ? t("All time") : w.label}
                </ToolbarChip>
              ))}
              <ToolbarSpacer />
              <ToolbarChip icon={<RefreshCw />} onClick={refresh}>
                {t("Refresh")}
              </ToolbarChip>
            </Toolbar>

            {runsLoading ? (
              <div className="p-5 space-y-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : runsError ? (
              // Antes do empty state, e não depois: sem este ramo a lista que
              // falhou chegava `undefined`, virava `[]` e a tela dizia
              // "nenhuma execução ainda" num projeto com centenas delas.
              <ErrorState
                message={t("Could not load the run history.")}
                onRetry={() => refetchRuns()}
              />
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-12 px-5 text-center">
                {/* Duas ausências diferentes. "O projeto nunca rodou nada" e
                    "este recorte não casou" mandam o consultor para lados
                    opostos, e a tela antiga dizia a mesma coisa nos dois. */}
                {hasFilter || (stats?.execution_count ?? 0) > 0 ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      {t("No run matches these filters.")}
                    </p>
                    {hasFilter && (
                      <Button variant="outline" size="sm" className="h-7" onClick={clearFilters}>
                        {t("Clear filters")}
                      </Button>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t("No run yet. Start an agent and the history fills in here.")}
                  </p>
                )}
              </div>
            ) : (
              <>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th style={{ width: 110 }}>{t("Run")}</th>
                      <th>{t("Agent")}</th>
                      <th>{t("Triggered by")}</th>
                      <th>{t("Model")}</th>
                      <th>{t("Duration")}</th>
                      <th>{t("Tokens")}</th>
                      <th>{t("Cost")}</th>
                      <th>{t("Status")}</th>
                      <th>{t("Started")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((run) => {
                      const tone = statusTone(run.status);
                      const duration = runDuration(run);
                      const model = runModelLabel(run.metrics ?? null);
                      return (
                        <tr key={run.id}>
                          <td>
                            <span
                              className="font-mono text-xs text-[#08254c] font-semibold"
                              title={run.id}
                            >
                              #{shortId(run.id)}
                            </span>
                          </td>
                          <td>
                            <strong>
                              {AGENT_DISPLAY_NAMES[run.agent_type] || run.agent_type}
                            </strong>
                          </td>
                          <td className="mono muted">{run.triggered_by_name || "—"}</td>
                          <td className="mono muted">{model || "—"}</td>
                          <td className="mono">
                            {duration ?? "—"}
                            {run.status === "running" && duration && (
                              <span className="muted"> {t("so far")}</span>
                            )}
                          </td>
                          <td className="mono">
                            {run.metrics?.total_tokens != null
                              ? detailedTokens(run.metrics.total_tokens)
                              : "—"}
                          </td>
                          <td className="mono">
                            {run.metrics?.cost_estimate != null
                              ? detailedUsd(run.metrics.cost_estimate)
                              : "—"}
                          </td>
                          <td>
                            {/* O motivo da falha vai no hover, não na célula: o
                                campo carrega texto cru de exceção em parte dos
                                casos, e a coluna viraria ilegível. */}
                            <span title={run.error_details ?? undefined}>
                              {tone ? (
                                <Pill tone={PILL_TONE[tone]} live={run.status === "running"}>
                                  {pipelineToneLabel(tone, t)}
                                </Pill>
                              ) : (
                                // Neutro e com o texto cru: sem tom próprio, não
                                // há cor honesta para um estado que a tela não
                                // conhece.
                                <Pill tone="muted">{run.status}</Pill>
                              )}
                            </span>
                          </td>
                          <td className="mono">{formatRelative(run.started_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="px-5 py-3 border-t border-[#f2f4f7]">
                  <CatalogPager
                    page={page}
                    pageSize={PAGE_SIZE}
                    total={total}
                    onPageChange={setPage}
                  />
                </div>
              </>
            )}
          </Card>
        </section>

        <Mantra>
          {t("EVERY RUN")} · <MantraAccent>{t("TRACED")}</MantraAccent> · {t("REPRODUCIBLE")}
        </Mantra>
      </div>
    </div>
  );
}
